import React from 'react';
import { useNavigate } from 'react-router-dom';

import { GuideAccordionList } from '@backrs_ui/grow-ui/src/components/GuidePage';
import { majorsGuideContent } from '@backrs_ui/grow-ui/src/pages/MajorsGuide/content';
import type { FAQSection } from '@backrs_ui/grow-ui/src/pages/MajorsGuide/content/types';

import { Box, Button, Typography, SxProps, Theme } from '@mui/material';

import { getIcon } from './IconMap';

type FAQAccordionSectionProps = Readonly<{
  faqSections: FAQSection[];
}>;

export const FAQAccordionSection: React.FC<FAQAccordionSectionProps> = ({ faqSections }) => {
  const navigate = useNavigate();

  return (
    <Box id="faq-accordion-section" sx={containerSx}>
      <GuideAccordionList
        idPrefix="faq-accordion"
        detailsSx={detailsSx}
        items={faqSections.map((section) => ({
          id: section.id,
          title: section.title,
          details: (
            <>
              <Typography variant="text-md-regular" sx={introSx}>
                {section.content.intro}
              </Typography>
              {section.content.subsections.map((subsection, idx) => (
                <Box key={idx} sx={subsectionSx}>
                  <Typography variant="text-lg-semibold" sx={subsectionHeadingSx}>
                    {subsection.heading}
                  </Typography>
                  <Typography variant="text-md-regular" sx={bodyTextSx}>
                    {subsection.content}
                  </Typography>
                </Box>
              ))}
              {section.content.bottomLine && (
                <Box sx={bottomLineSx}>
                  <Typography variant="text-lg-semibold">
                    {majorsGuideContent.labels.bottomLine}
                  </Typography>
                  <Typography variant="text-md-regular" sx={bodyTextSx}>
                    {section.content.bottomLine}
                  </Typography>
                </Box>
              )}
              {section.content.ctaButton && (
                <Button
                  id={`faq-accordion-cta-${section.id}-btn`}
                  variant="contained"
                  size="md"
                  hierarchy="Secondary color"
                  startIcon={
                    section.content.ctaButton.icon
                      ? getIcon(section.content.ctaButton.icon, 16)
                      : undefined
                  }
                  onClick={() => {
                    const { route } = section.content.ctaButton ?? {};

                    if (route) {
                      navigate(route);
                    }
                  }}
                >
                  {section.content.ctaButton.label}
                </Button>
              )}
            </>
          ),
        }))}
      />
    </Box>
  );
};

const containerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
};

const detailsSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const introSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const bodyTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-secondary'],
});

const subsectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});

const subsectionHeadingSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-primary'],
});

const bottomLineSx: SxProps<Theme> = ({ backrs }) => ({
  p: backrs.spacing['spacing-lg'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary'],
  borderRadius: backrs.radius['radius-md'],
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xs'],
});
